// Layer schema matches the certificate designer exactly (x/y/w/h in canvas pixels).
// Canvas space: 794 × 1123 px (A4 at 96 DPI).

export const CANVAS_W = 794
export const CANVAS_H = 1123
export const DISPLAY_W = 760   // px on screen — scale = DISPLAY_W / CANVAS_W

export type LayerType =
  | "image"
  | "text"
  | "dynamic-text"
  | "shape"
  | "line"
  | "header"
  | "student_info"
  | "subject_table"
  | "grade_key"
  | "signatures"
  | "attendance"
  | "folder"          // RMS organizer row — groups layers, invisible on canvas
  | `rms_${string}`   // imported RMS layers — rendered as color-coded placeholders

export type Layer = {
  id: string
  type: LayerType
  x: number
  y: number
  w: number
  h: number
  rotation: number
  opacity: number
  locked: boolean
  _inherited?: boolean   // read-only master template layer

  // ── RMS fidelity fields (preserved from the source layer) ──
  name?: string | null            // human layer name shown in the panel
  hidden?: boolean                // eye toggle — not drawn on canvas when true
  groupId?: string | null         // folder membership (points at a folder layer id)
  isFolderAnchor?: boolean        // this layer is its folder's drag anchor
  anchorParentId?: string | null  // position-cascade parent
  anchorFracX?: number | null
  anchorFracY?: number | null
  sample?: string | null          // preview value for {{token}} text
  text?: string | null            // raw token text (content mirrors this)
  // original RMS % geometry (kept for exact re-projection)
  topPct?: number | null
  leftPct?: number | null
  widthPct?: number | null
  heightPct?: number | null

  // image
  src?: string
  fit?: "contain" | "cover" | "fill"

  // text / dynamic-text
  content?: string
  field?: string
  prefix?: string
  suffix?: string
  fontFamily?: string
  fontSize?: number
  fontWeight?: string
  fontStyle?: string
  color?: string
  textAlign?: string
  lineHeight?: number
  letterSpacing?: number
  autoFit?: boolean
  shadow?: { enabled: boolean; x: number; y: number; blur: number; color: string }
  gradient?: { enabled: boolean; from: string; to: string; angle: number }

  // shape
  shape?: "rectangle" | "ellipse"
  fill?: string
  stroke?: string
  strokeWidth?: number
  borderRadius?: number

  // line
  thickness?: number
  rounded?: boolean

  // report-card specific
  headerColor?: string    // header / subject_table background
  accentColor?: string
  showPhoto?: boolean

  // rms_* imported layers — stores original RMS props for the PDF renderer
  _rmsProps?: Record<string, unknown>
}

export type CanvasDefinition = {
  canvas: { width: number; height: number; background: string }
  assets: string[]   // S3 keys of uploaded images for this template
  layers: Layer[]
}
